import { useCallback, useEffect, useMemo, useState } from 'react';

import { DialogHeader } from '@components/full-view-dialog/dialog-header';
import { DialogContent } from '@components/full-view-dialog/dialog-content';
import { FullViewDialog } from '@components/full-view-dialog/full-view-dialog';
import { Divider } from '@components/divider/divider';
import CustomInput from '@components/custom-input/custom-input.component';
import Toggle from '@components/toggle/toggle';
import FlatButton from '@components/flat-button/flat-button';
import BasicButton from '@components/basic-button/basic-button.component';
import { Select } from '@components/custom-select/select';
import {
    SelectItem,
    SelectItemType,
} from '@components/custom-select/select-item';
import { useForm } from '@hooks/form';
import { Plan, UpdatePlan, CreatePlan } from '@app-types/service.type';
import { validateSchema } from '@utils/validate-schema';
import { useServicesStore } from '@stores/services-store';

import { createPlanSchema, editPlanSchema } from '../../config';

import styles from './plan-dialog.module.scss';

type Props = {
    shown: boolean;
    plan?: Plan;
    updating: boolean;
    onUpdate: (data: { id: number; data: UpdatePlan }) => void;
    onCreate: (data: CreatePlan) => void;
    onClose: () => void;
};

const PlanDialog = ({
    shown,
    plan,
    updating,
    onUpdate,
    onCreate,
    onClose,
}: Props) => {
    const { planFrequencies } = useServicesStore();

    const formatPrice = useCallback((price?: number) => {
        return price?.toFixed(2) ?? '0.00';
    }, []);

    const frequencySelectOptions: SelectItemType[] = useMemo(() => {
        return planFrequencies.map((frequency) => ({
            id: frequency.id,
            label: frequency.name,
            supportingText: `(${frequency.value} meses)`,
            selected: frequency.id === plan?.frequencyId,
        }));
    }, [planFrequencies, plan]);

    const billingOptions: SelectItemType[] = useMemo(() => {
        return [
            {
                id: 'recurring',
                label: 'Recurrente',
                supportingText: '(Mensualmente)',
                selected: plan?.type.includes('recurring'),
            },
            {
                id: 'one_time',
                label: 'Anual',
                supportingText: '(Única vez)',
                selected: plan?.type === 'one_time',
            },
        ];
    }, [plan]);

    const { formData, setFormData, handleChange } = useForm({
        defaultValues: {
            description: '',
            frequencyId: -1,
            billingType: 'one_time',
            isActive: false,
            isPublic: false,
            isForBusiness: false,
            price: '0.00',
        },
    });

    const [errors, setErrors] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        if (!plan) return;
        setFormData({
            description: plan.description ?? '',
            price: formatPrice(plan.prices[0]?.price),
            frequencyId: plan.frequencyId,
            billingType: plan.type,
            isActive: plan.active,
            isPublic: plan.isPublic,
            isForBusiness: plan.isForBusiness,
        });
    }, [plan]);

    const onSubmit = () => {
        setErrors(new Map());
        if (!plan) {
            return createPlan();
        }
        updatePlan();
    };

    const updatePlan = async () => {
        const errors = validateSchema(formData, editPlanSchema);
        if (errors) return setErrors(errors);
        onUpdate({
            id: plan!.id,
            data: {
                description: formData.description,
                frequencyId: Number(formData.frequencyId),
                billingType: formData.billingType,
                isActive: formData.isActive,
                isPublic: formData.isPublic,
                isForBusiness: formData.isForBusiness,
                price: {
                    amount: Number(formData.price),
                    currency: 'USD',
                },
            },
        });
    };

    const createPlan = () => {
        const errors = validateSchema(formData, createPlanSchema);
        if (errors) return setErrors(errors);
        const payload: CreatePlan = {
            description: formData.description,
            frequencyId: formData.frequencyId,
            billingType: formData.billingType,
            isActive: formData.isActive,
            isPublic: formData.isPublic,
            isForBusiness: formData.isForBusiness,
            price: {
                amount: Number(formData.price),
                currency: 'USD',
            },
        };
        onCreate(payload);
    };

    const showError = useCallback(
        (name: string) => {
            if (!errors.has(name)) return <></>;
            return <span className={styles.error}>{errors.get(name)}</span>;
        },
        [errors]
    );

    return (
        <FullViewDialog shown={shown} onClose={onClose} maxWidth={600}>
            <DialogHeader>
                <h3 className={styles.title}>Editar plan</h3>
                <label className={styles.subtitle}>
                    Actualizar datos del plan
                </label>
            </DialogHeader>
            <DialogContent>
                <Divider />
                <form className={styles.form}>
                    <div className={styles.formRow}>
                        <label>Nombre del plan</label>
                        <CustomInput
                            value={formData.description}
                            width="100%"
                            onChange={handleChange('description')}
                        />
                        {showError('description')}
                    </div>
                    <div className={styles.formRow}>
                        <label>Periodo de la suscripción</label>
                        <Select
                            placeholder="Período de la suscripción"
                            items={frequencySelectOptions}
                            onSelect={(item) =>
                                handleChange('frequencyId')(item.id)
                            }
                        >
                            {frequencySelectOptions.map((option) => (
                                <SelectItem
                                    key={option.id}
                                    id={option.id}
                                    supportingText={option.supportingText}
                                >
                                    {option.label}
                                </SelectItem>
                            ))}
                        </Select>
                        {showError('frequencyId')}
                    </div>
                    <div className={styles.formRow}>
                        <label>Frecuencia de cobro</label>
                        <Select
                            placeholder="Frecuencia de cobro"
                            items={billingOptions}
                            onSelect={(item) =>
                                handleChange('billingType')(item.id)
                            }
                        >
                            {billingOptions.map((opt) => (
                                <SelectItem key={opt.id} id={opt.id}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </Select>
                        {showError('billingType')}
                    </div>
                    <div className={styles.formRow}>
                        <label>Precio (USD)</label>
                        <CustomInput
                            value={formData.price}
                            width="100%"
                            type="number"
                            onChange={handleChange('price')}
                        />
                        {showError('price')}
                    </div>
                    <div className={styles.toggles}>
                        <div className={styles.toggle}>
                            <label>¿Está activo?</label>
                            <Toggle
                                value={formData.isActive}
                                onToggle={handleChange('isActive')}
                            />
                            {showError('isActive')}
                        </div>
                        <Divider direction="vertical" />
                        <div className={styles.toggle}>
                            <label>¿Es público?</label>
                            <Toggle
                                value={formData.isPublic}
                                onToggle={handleChange('isPublic')}
                            />
                            {showError('isPublic')}
                        </div>
                        <Divider direction="vertical" />
                        <div className={styles.toggle}>
                            <label>¿Es para empresas?</label>
                            <Toggle
                                value={formData.isForBusiness}
                                onToggle={handleChange('isForBusiness')}
                            />
                            {showError('isForBusiness')}
                        </div>
                    </div>
                </form>
                <footer className={styles.footer}>
                    <FlatButton onClick={onClose}>Descartar</FlatButton>
                    <BasicButton onClick={onSubmit} loading={updating}>
                        {plan ? 'Actualizar' : 'Crear'} plan
                    </BasicButton>
                </footer>
            </DialogContent>
        </FullViewDialog>
    );
};

export { PlanDialog };
